import React, { useState, useRef, useEffect } from 'react';
import { User, ProfileFormData } from '@/types';
import { IoCamera, IoDownload, IoTrash, IoChevronUp, IoChevronDown, IoInformationCircle } from 'react-icons/io5';
import { useAuth } from '@/features/auth/context/AuthContext';
import api from '@/utils/api.utils';
import { showApiError, ToastService } from '@/utils/toast';
import CollapsibleSection from '../../../components/CollapsibleSection';
import ActivityCardsDisplay from '@/components/ActivityCardsDisplay';
import { useActivityData } from '@/hooks/useActivityData';
import { Activity } from '@/services/activityService';
import AddressField from './AddressField';
import CountrySelect from '@/components/CountrySelect';
import {
  suffixOptions,
  genderOptions,
  pronounOptions,
  legalSexOptions,
  militaryOptions,
  enrollmentStatusOptions,
  proficiencyOptions,
  citizenshipOptions,
  ethnicityOptions,
} from '@/data/profileOptions';

interface ProfileTabProps {
  user: User;
}

const ProfileTab: React.FC<ProfileTabProps> = ({ user }) => {
  const { updateUser } = useAuth();
  const [isProfileExpanded, setIsProfileExpanded] = useState(true);
  const [photoURL, setPhotoURL] = useState<string | undefined>(user.photoURL);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [showCitizenshipTooltip, setShowCitizenshipTooltip] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [formData, setFormData] = useState<ProfileFormData>({
    first_name: user.first_name || '',
    middle_name: user.middle_name || '',
    last_name: user.last_name || '',
    suffix: user.suffix || 'None',
    has_preferred_name: user.has_preferred_name || false,
    preferred_first_name: user.preferred_first_name || '',
    preferred_middle_name: user.preferred_middle_name || '',
    preferred_last_name: user.preferred_last_name || '',
    has_former_name: user.has_former_name || false,
    former_first_name: user.former_first_name || '',
    former_middle_name: user.former_middle_name || '',
    former_last_name: user.former_last_name || '',
    date_of_birth: user.date_of_birth || '',
    phone_number: user.phone_number || '',
    home_address: user.home_address || null,
    mailing_address: user.mailing_address || null,
    gender: user.gender || '',
    custom_gender: user.custom_gender || '',
    pronouns: user.pronouns || '',
    custom_pronouns: user.custom_pronouns || '',
    legal_sex: user.legal_sex || '',
    military_status: user.military_status || 'None',
    military_enrollment_status: user.military_enrollment_status || '',
    ethnicities: user.ethnicities || [],
    latino_nationality: user.latino_nationality || '',
    first_language: user.first_language || '',
    language_proficiency: user.language_proficiency || '',
    other_languages: user.other_languages || [],
    birth_country: user.birth_country || '',
    birth_city: user.birth_city || '',
    years_in_us: user.years_in_us || '',
    citizenship_status: user.citizenship_status || '',
  });

  const [showMailingAddress, setShowMailingAddress] = useState(!!user.mailing_address);
  const [languageInput, setLanguageInput] = useState('');
  const [languageAbilities, setLanguageAbilities] = useState<Record<string, { firstLanguage: boolean; speak: boolean; read: boolean; write: boolean; spokenAtHome: boolean }>>(() => {
    const obj: Record<string, any> = {};
    if (formData.first_language) {
      obj[formData.first_language] = { firstLanguage: true, speak: true, read: false, write: false, spokenAtHome: false };
    }
    (formData.other_languages || []).forEach((l: string) => {
      if (!obj[l]) obj[l] = { firstLanguage: false, speak: true, read: false, write: false, spokenAtHome: false };
    });
    return obj;
  });

  useEffect(() => {
    if (formData.first_language && !languageAbilities[formData.first_language]) {
      setLanguageAbilities(prev => ({
        ...prev,
        [formData.first_language]: { firstLanguage: true, speak: true, read: false, write: false, spokenAtHome: false }
      }));
    }
  }, [formData.first_language]);

  const LANGUAGES = [
    'English','Spanish','Chinese','Hindi','Arabic','Bengali','Portuguese','Russian','Japanese','Punjabi','German','Javanese','Korean','Vietnamese','Telugu','Marathi','Tamil','Urdu','Italian','Gujarati','Polish','Ukrainian','Persian','Malay','Indonesian','Thai','Turkish','Dutch','Greek','Hebrew','Swahili','Filipino','Romanian','Hungarian','Czech','Danish','Finnish','Norwegian','Swedish'
  ];
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);
  const languageDropdownRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      const target = e.target as Node | null;
      if (languageDropdownRef.current && !languageDropdownRef.current.contains(target)) {
        setShowLanguageDropdown(false);
      }
    };
    document.addEventListener('click', onDocClick);
    return () => document.removeEventListener('click', onDocClick);
  }, []);

  useEffect(() => {
    const loadLanguages = async () => {
      try {
        const res = await api.get('/api/v1/auth/me/languages');
        const data = res.data || [];
        const otherLangs: string[] = [];
        const abilitiesMap: Record<string, any> = {};
        let firstLang: string | undefined = undefined;

        (data || []).forEach((row: any) => {
          const name = row.name;
          if (!name) return;
          if (row.is_first_language) {
            firstLang = name;
            abilitiesMap[name] = row.abilities || { firstLanguage: true, speak: true, read: false, write: false, spokenAtHome: false };
          } else {
            otherLangs.push(name);
            abilitiesMap[name] = row.abilities || { firstLanguage: false, speak: true, read: false, write: false, spokenAtHome: false };
          }
        });

        setFormData(prev => ({ ...prev, other_languages: otherLangs, first_language: firstLang || prev.first_language }));
        setLanguageAbilities(prev => ({ ...prev, ...abilitiesMap }));
      } catch (e) {
        console.warn('Failed to load languages', e);
      }
    };
    loadLanguages();
  }, []);

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError('File must be an image.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError('File must be smaller than 5MB.');
      return;
    }

    setError(null);
    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('photo', file);

      const response = await api.post('/api/v1/auth/me/upload-photo', formData);
      const newPhotoURL = response.data.photoURL;

      setPhotoURL(newPhotoURL);
      updateUser({ ...user, photoURL: newPhotoURL });
    } catch (err: any) {
      showApiError(err);
      setError(err?.response?.data?.detail || 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleRemovePhoto = async () => {
    setUploading(true);
    setError(null);

    try {
      await api.delete('/api/v1/auth/me/photo');
      setPhotoURL(undefined);
      updateUser({ ...user, photoURL: undefined });
    } catch (err: any) {
      showApiError(err);
      setError(err?.response?.data?.detail || 'Failed to remove photo.');
    } finally {
      setUploading(false);
    }
  };

  const handleImportData = () => {
    console.log('Import data clicked');
  };

  const handleInputChange = (field: keyof ProfileFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleEthnicityChange = (ethnicity: string, checked: boolean) => {
    setFormData(prev => ({
      ...prev,
      ethnicities: checked
        ? [...prev.ethnicities, ethnicity]
        : prev.ethnicities.filter(e => e !== ethnicity)
    }));
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    setError(null);

    try {
      const payload: any = { ...formData } as any;

      Object.keys(payload).forEach((k) => {
        const key = k as keyof typeof payload;
        if (payload[key] === '') {
          delete payload[key];
        }
      });

      if (payload.years_in_us !== undefined) {
        if (payload.years_in_us === null || payload.years_in_us === undefined) {
          delete payload.years_in_us;
        } else {
          const n = Number(payload.years_in_us);
          if (!isNaN(n)) payload.years_in_us = n;
          else delete payload.years_in_us;
        }
      }
      const response = await api.patch('/api/v1/auth/me', payload);
      if (response.data) {
        const updatedData = {
          ...formData,
          years_in_us: formData.years_in_us ? Number(formData.years_in_us) : undefined
        };
        updateUser({ ...user, ...updatedData });
        try {
          const languages: any[] = [];
          if (formData.first_language) {
            languages.push({
              name: formData.first_language,
              is_first_language: true,
              proficiency: formData.language_proficiency || undefined,
              abilities: languageAbilities[formData.first_language] || {}
            });
          }
          (formData.other_languages || []).forEach((lang: string) => {
            languages.push({
              name: lang,
              is_first_language: false,
              proficiency: undefined,
              abilities: languageAbilities[lang] || {}
            });
          });

          await api.post('/api/v1/auth/me/languages/bulk', languages);
          ToastService.showSuccess('Profile saved successfully');
        } catch (e: any) {
          console.error('Failed to sync languages', e);
          showApiError(e);
          setError('Profile saved but languages failed to persist.');
        }
      }
    } catch (err: any) {
      if (err?.response?.status === 422) {
        showApiError('Invalid request data');
        setError('Invalid request data');
      } else {
        showApiError(err);
        setError(err?.response?.data?.detail || 'Failed to save profile.');
      }
    } finally {
      setSaving(false);
    }
  };


  return (
    <div className="profile-tab">
      <div className="settings-section">
        <div
          className="settings-section-header"
          onClick={() => setIsProfileExpanded(!isProfileExpanded)}
        >
          <div>
            <h2 className="settings-section-title">Profile</h2>
            <p className="settings-section-description">
              Complete your profile information for college applications
            </p>
          </div>
          {isProfileExpanded ? <IoChevronUp /> : <IoChevronDown />}
        </div>

        {isProfileExpanded && (
          <div className="settings-section-content">
            <div className="profile-photo-section">
              <h3 className="profile-section-subtitle">Profile Photo</h3>

              <div className="profile-photo-container">
                <div className="profile-photo-wrapper">
                  {photoURL ? (
                    <img
                      src={photoURL}
                      alt={user.name}
                      className="profile-photo-image"
                    />
                  ) : (
                    <div className="profile-photo-placeholder">
                      {user.name?.charAt(0)?.toUpperCase() || 'U'}
                    </div>
                  )}
                  <button
                    className="profile-photo-camera-button"
                    onClick={handleUploadClick}
                    disabled={uploading}
                    title="Change photo"
                  >
                    <IoCamera />
                  </button>
                </div>

                <div className="profile-photo-details">
                  <h4 className="profile-photo-title">Update Profile Picture</h4>
                  <p className="profile-photo-description">
                    Upload a professional photo that represents you. Recommended size: 400×400px, max 5MB
                  </p>

                  {error && <p className="profile-photo-error">{error}</p>}

                  <div className="profile-photo-actions">
                    <button
                      className="profile-photo-button primary"
                      onClick={handleUploadClick}
                      disabled={uploading}
                    >
                      <IoDownload />
                      {uploading ? 'Uploading...' : 'Upload New Photo'}
                    </button>
                    <button
                      className="profile-photo-button secondary"
                      onClick={handleRemovePhoto}
                      disabled={uploading || !photoURL}
                    >
                      <IoTrash />
                      Remove
                    </button>
                  </div>

                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    style={{ display: 'none' }}
                  />
                </div>
              </div>
            </div>

            <div className="common-app-section">
              <h3 className="profile-section-subtitle">Common App Integration</h3>
              <div className="common-app-card">
                <div className="common-app-icon">
                  <img src="/image.png" alt="Common App" style={{ width: 48, height: 48, objectFit: 'contain' }} />
                </div>
                <div className="common-app-details">
                  <h4 className="common-app-title">Import from Common App</h4>
                  <p className="common-app-description">
                    Automatically import your profile data, test scores, activities, and college list from Common Application
                  </p>
                  <p className="common-app-features-text">
                    ✓ Personal Information ✓ Test Scores ✓ Activities ✓ College List
                  </p>
                </div>
                <button
                  className="common-app-import-button"
                  onClick={handleImportData}
                >
                  <IoDownload />
                  Import Data
                </button>
              </div>
            </div>

            <div className="profile-form-section">
              <h3 className="profile-form-section-title">Legal Name</h3>
              <div className="profile-form-row">
                <div className="profile-form-field">
                  <label className="profile-form-label">Legal First Name</label>
                  <input
                    type="text"
                    className="profile-form-input"
                    value={formData.first_name}
                    onChange={(e) => handleInputChange('first_name', e.target.value)}
                  />
                </div>
                <div className="profile-form-field">
                  <label className="profile-form-label">Middle Name</label>
                  <input
                    type="text"
                    className="profile-form-input"
                    value={formData.middle_name}
                    onChange={(e) => handleInputChange('middle_name', e.target.value)}
                  />
                </div>
              </div>

              <div className="profile-form-row">
                <div className="profile-form-field">
                  <label className="profile-form-label">Last Name</label>
                  <input
                    type="text"
                    className="profile-form-input"
                    value={formData.last_name}
                    onChange={(e) => handleInputChange('last_name', e.target.value)}
                  />
                </div>
                <div className="profile-form-field">
                  <label className="profile-form-label">Suffix</label>
                  <select
                    className="profile-form-select"
                    value={formData.suffix}
                    onChange={(e) => handleInputChange('suffix', e.target.value)}
                  >
                    {suffixOptions.map(option => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="profile-form-toggle-section">
                <div className="profile-form-toggle-header">
                  <div>
                    <h4 className="profile-form-toggle-title">Preferred Name</h4>
                    <p className="profile-form-toggle-description">
                      Do you have a preferred name different from your legal name?
                    </p>
                  </div>
                  <label className="profile-toggle-switch">
                    <input
                      type="checkbox"
                      checked={formData.has_preferred_name}
                      onChange={(e) => handleInputChange('has_preferred_name', e.target.checked)}
                    />
                    <span className="profile-toggle-slider"></span>
                  </label>
                </div>
                {formData.has_preferred_name && (
                  <div className="profile-form-field" style={{ marginTop: '1rem' }}>
                    <label className="profile-form-label">Preferred Name</label>
                    <input
                      type="text"
                      className="profile-form-input"
                      placeholder="Enter your preferred name..."
                      value={formData.preferred_first_name}
                      onChange={(e) => handleInputChange('preferred_first_name', e.target.value)}
                    />
                  </div>
                )}
              </div>

              <div className="profile-form-toggle-section">
                <div className="profile-form-toggle-header">
                  <div>
                    <h4 className="profile-form-toggle-title">Former Legal Name</h4>
                    <p className="profile-form-toggle-description">
                      Do you have materials under a former legal name?
                    </p>
                  </div>
                  <label className="profile-toggle-switch">
                    <input
                      type="checkbox"
                      checked={formData.has_former_name}
                      onChange={(e) => handleInputChange('has_former_name', e.target.checked)}
                    />
                    <span className="profile-toggle-slider"></span>
                  </label>
                </div>
                {formData.has_former_name && (
                  <div style={{ marginTop: '1rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    <div className="profile-form-field">
                      <label className="profile-form-label">Former Legal Name</label>
                      <input
                        type="text"
                        className="profile-form-input"
                        placeholder="Enter your former legal name..."
                        value={formData.former_first_name}
                        onChange={(e) => handleInputChange('former_first_name', e.target.value)}
                      />
                    </div>
                    <div className="profile-form-field">
                      <label className="profile-form-label">Explanation</label>
                      <textarea
                        className="profile-form-input"
                        rows={3}
                        placeholder="If you have transcripts, test scores, or other materials under a different name, please explain..."
                        value={formData.former_last_name}
                        onChange={(e) => handleInputChange('former_last_name', e.target.value)}
                        style={{ resize: 'vertical', minHeight: '80px' }}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="profile-form-section">
              <h3 className="profile-form-section-title">Personal Information</h3>
              <div className="profile-form-row">
                <div className="profile-form-field">
                  <label className="profile-form-label">Date of Birth</label>
                  <input
                    type="date"
                    className="profile-form-input"
                    value={formData.date_of_birth}
                    onChange={(e) => handleInputChange('date_of_birth', e.target.value)}
                  />
                </div>
                <div className="profile-form-field">
                  <label className="profile-form-label">Phone Number</label>
                  <input
                    type="tel"
                    className="profile-form-input"
                    placeholder="e.g., +1 (555) 123-4567 or +44 20 7946 0958"
                    value={formData.phone_number}
                    onChange={(e) => handleInputChange('phone_number', e.target.value)}
                  />
                </div>
              </div>

              <AddressField
                label="Home Address"
                value={formData.home_address}
                onSave={(addr) => handleInputChange('home_address', addr)}
              />

              <div className="profile-form-toggle-section">
                <div className="profile-form-toggle-header">
                  <div>
                    <h4 className="profile-form-toggle-title">Separate Mailing Address</h4>
                    <p className="profile-form-toggle-description">
                      Do you have a different mailing address?
                    </p>
                  </div>
                  <label className="profile-toggle-switch">
                    <input
                      type="checkbox"
                      checked={showMailingAddress}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setShowMailingAddress(checked);
                        if (!checked) handleInputChange('mailing_address', null);
                      }}
                    />
                    <span className="profile-toggle-slider"></span>
                  </label>
                </div>
                {showMailingAddress && (
                  <div style={{ marginTop: '1rem' }}>
                    <AddressField
                      label="Mailing Address"
                      value={formData.mailing_address}
                      onSave={(addr) => handleInputChange('mailing_address', addr)}
                    />
                  </div>
                )}
              </div>
            </div>

            <div className="profile-form-section">
              <h3 className="profile-form-section-title">Gender and Identity</h3>
              <div className="profile-form-row">
                <div className="profile-form-field">
                  <label className="profile-form-label">Gender</label>
                  <select
                    className="profile-form-select"
                    value={formData.gender}
                    onChange={(e) => handleInputChange('gender', e.target.value)}
                  >
                    <option value="">Select gender...</option>
                    {genderOptions.map(option => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                  {formData.gender === 'I identify as another gender' && (
                    <div className="profile-form-field" style={{ marginTop: '0.5rem' }}>
                      <input
                        type="text"
                        className="profile-form-input"
                        placeholder="Please specify your gender"
                        value={formData.custom_gender}
                        onChange={(e) => handleInputChange('custom_gender', e.target.value)}
                      />
                    </div>
                  )}
                </div>
                <div className="profile-form-field">
                  <label className="profile-form-label">Pronouns</label>
                  <select
                    className="profile-form-select"
                    value={formData.pronouns}
                    onChange={(e) => handleInputChange('pronouns', e.target.value)}
                  >
                    <option value="">Select pronouns...</option>
                    {pronounOptions.map(option => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                  {formData.pronouns === 'I go by other pronouns' && (
                    <div className="profile-form-field" style={{ marginTop: '0.5rem' }}>
                      <input
                        type="text"
                        className="profile-form-input"
                        placeholder="Please specify your pronouns"
                        value={formData.custom_pronouns}
                        onChange={(e) => handleInputChange('custom_pronouns', e.target.value)}
                      />
                    </div>
                  )}
                </div>
              </div>

              <div className="profile-form-field">
                <label className="profile-form-label">Legal Sex</label>
                <select
                  className="profile-form-select"
                  value={formData.legal_sex}
                  onChange={(e) => handleInputChange('legal_sex', e.target.value)}
                >
                  <option value="">Select legal sex...</option>
                  {legalSexOptions.map(option => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="profile-form-section">
              <h3 className="profile-form-section-title">Background</h3>
              <div className="profile-form-field">
                <label className="profile-form-label">Military Service Status</label>
                <select
                  className="profile-form-select"
                  value={formData.military_status}
                  onChange={(e) => handleInputChange('military_status', e.target.value)}
                >
                  {militaryOptions.map(option => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              </div>
              {formData.military_status === 'Currently serving' && (
                <div className="profile-form-field" style={{ marginTop: '1rem' }}>
                  <label className="profile-form-label">Expected Status At Time of Enrollment</label>
                  <select
                    className="profile-form-select"
                    value={formData.military_enrollment_status}
                    onChange={(e) => handleInputChange('military_enrollment_status', e.target.value)}
                  >
                    <option value="">Select status</option>
                    {enrollmentStatusOptions.map(option => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            <div className="profile-form-section">
              <h3 className="profile-form-section-title">Ethnicity</h3>
              <p className="profile-form-section-description">
                Ethnicity (Select all that apply)
              </p>
              <div className="profile-ethnicity-grid">
                {ethnicityOptions.map(ethnicity => (
                  <label key={ethnicity} className="profile-checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.ethnicities.includes(ethnicity)}
                      onChange={(e) => handleEthnicityChange(ethnicity, e.target.checked)}
                      className="profile-checkbox"
                    />
                    <span>{ethnicity}</span>
                  </label>
                ))}
              </div>
              {formData.ethnicities.includes('Latino/a/x') && (
                <div className="profile-form-field" style={{ marginTop: '1.5rem' }}>
                  <label className="profile-form-label">Latino Nationality</label>
                  <input
                    type="text"
                    className="profile-form-input"
                    placeholder="e.g., Mexican, Puerto Rican, Cuban"
                    value={formData.latino_nationality}
                    onChange={(e) => handleInputChange('latino_nationality', e.target.value)}
                  />
                </div>
              )}
            </div>

            <div className="profile-form-section">
              <h3 className="profile-form-section-title">Language Information</h3>
              <div className="profile-form-row">
                <div className="profile-form-field">
                  <label className="profile-form-label">First Language</label>
                  <input
                    type="text"
                    className="profile-form-input"
                    placeholder="Enter your first language"
                    value={formData.first_language}
                    onChange={(e) => handleInputChange('first_language', e.target.value)}
                  />
                </div>
                <div className="profile-form-field">
                  <label className="profile-form-label">Proficiency Level</label>
                  <select
                    className="profile-form-select"
                    value={formData.language_proficiency}
                    onChange={(e) => handleInputChange('language_proficiency', e.target.value)}
                  >
                    <option value="">Select proficiency...</option>
                    {proficiencyOptions.map(option => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="profile-form-field">
                <label className="profile-form-label">Do you know any other languages?</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {formData.other_languages.map(lang => (
                      <span key={lang} className="profile-language-tag">
                        {lang}
                        <button type="button" className="profile-language-tag-remove" onClick={() => {
                          const newArr = formData.other_languages.filter(l => l !== lang);
                          handleInputChange('other_languages', newArr);
                          setLanguageAbilities(prev => { const copy = { ...prev }; delete copy[lang]; return copy; });
                        }}>×</button>
                      </span>
                    ))}
                  </div>

                  <div ref={languageDropdownRef} style={{ position: 'relative' }}>
                    <div
                      role="button"
                      tabIndex={0}
                      onClick={() => setShowLanguageDropdown(s => !s)}
                      onKeyDown={(e) => { if (e.key === 'Enter') setShowLanguageDropdown(s => !s); }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '0.75rem 1rem',
                        borderRadius: 10,
                        border: '1px solid rgba(0,0,0,0.12)',
                        background: 'var(--surface-secondary)',
                        cursor: 'pointer'
                      }}
                    >
                      <div style={{ color: formData.other_languages.length === 0 ? 'rgba(0,0,0,0.4)' : 'inherit' }}>
                        {formData.other_languages.length === 0 ? 'Select languages...' : ''}
                      </div>
                      <div style={{ marginLeft: 12, opacity: 0.65 }}>▾</div>
                    </div>

                    {showLanguageDropdown && (
                      <div style={{ position: 'absolute', left: 0, right: 0, marginTop: 8, background: '#fff', border: '1px solid rgba(0,0,0,0.08)', borderRadius: 10, zIndex: 40, maxHeight: 260, overflow: 'auto', boxShadow: '0 6px 18px rgba(0,0,0,0.08)' }}>
                        <div style={{ padding: 10 }}>
                          <input
                            type="text"
                            placeholder="Search languages..."
                            value={languageInput}
                            onChange={(e) => setLanguageInput(e.target.value)}
                            className="profile-form-input"
                            style={{ width: '100%', padding: '0.6rem 0.75rem', borderRadius: 8, border: '1px solid rgba(0,0,0,0.06)' }}
                          />
                        </div>
                        <div>
                          {LANGUAGES.filter(l => l.toLowerCase().includes(languageInput.toLowerCase())).map(lang => {
                            const selected = formData.other_languages.includes(lang);
                            return (
                              <div
                                key={lang}
                                className={`profile-language-dropdown-option${selected ? ' selected' : ''}`}
                                onClick={() => {
                                  if (!selected) {
                                    handleInputChange('other_languages', [...formData.other_languages, lang]);
                                    setLanguageAbilities(prev => ({ ...prev, [lang]: { firstLanguage: false, speak: true, read: false, write: false, spokenAtHome: false } }));
                                  }
                                  setLanguageInput('');
                                  setShowLanguageDropdown(false);
                                }}
                              >
                                <input
                                  type="checkbox"
                                  checked={selected}
                                  onChange={() => {}}
                                  className="profile-language-dropdown-checkbox"
                                />
                                <span>{lang}</span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {(formData.other_languages || []).map((lang: string) => (
                <div key={lang} className="profile-form-field" style={{ marginTop: '1rem' }}>
                  <label className="profile-form-label">How proficient are you in {lang}?</label>
                  <div className="profile-proficiency-grid">
                    {['First Language', 'Speak', 'Read', 'Write', 'Spoken At Home'].map((label) => {
                      const key = label === 'First Language' ? 'firstLanguage' : (label === 'Spoken At Home' ? 'spokenAtHome' : label.toLowerCase());
                      const abilities = languageAbilities[lang] || { firstLanguage: false, speak: false, read: false, write: false, spokenAtHome: false };
                      const checked = Boolean((abilities as any)[key]);
                      return (
                        <label key={`${lang}-${label}`} className="profile-checkbox-label">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => setLanguageAbilities(prev => ({ ...prev, [lang]: { ...(prev[lang] || {}), [key]: !checked } }))}
                            className="profile-checkbox"
                          />
                          <span>{label}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              ))}

            </div>

            <div className="profile-form-section">
              <h3 className="profile-form-section-title">Birth and Citizenship Information</h3>
              <div className="profile-form-row">
                <div className="profile-form-field">
                  <label className="profile-form-label">Birth Country</label>
                  <CountrySelect
                    value={formData.birth_country}
                    onChange={(val) => handleInputChange('birth_country', val)}
                  />
                </div>
                <div className="profile-form-field">
                  <label className="profile-form-label">City of Birth</label>
                  <input
                    type="text"
                    className="profile-form-input"
                    placeholder="Enter city of birth"
                    value={formData.birth_city}
                    onChange={(e) => handleInputChange('birth_city', e.target.value)}
                  />
                </div>
              </div>

              <div className="profile-form-row">
                <div className="profile-form-field">
                  <label className="profile-form-label">Years Lived in US</label>
                  <input
                    type="number"
                    className="profile-form-input"
                    placeholder="Enter number of years"
                    value={formData.years_in_us}
                    onChange={(e) => handleInputChange('years_in_us', e.target.value)}
                  />
                </div>
                <div className="profile-form-field">
                  <label className="profile-form-label">
                    Citizenship Status
                    <span
                      className="profile-info-icon-wrapper"
                      onMouseEnter={() => setShowCitizenshipTooltip(true)}
                      onMouseLeave={() => setShowCitizenshipTooltip(false)}
                    >
                      <IoInformationCircle className="profile-info-icon" />
                      {showCitizenshipTooltip && (
                        <div className="profile-tooltip">
                          <div className="profile-tooltip-title">Citizenship Status Definitions:</div>
                          <div className="profile-tooltip-item"><span className="profile-tooltip-term">U.S. citizen or U.S. national:</span> Born in the U.S. or naturalized citizen, including U.S. nationals from territories</div>
                          <div className="profile-tooltip-item"><span className="profile-tooltip-term">U.S. dual citizen:</span> Holds citizenship in both the U.S. and another country</div>
                          <div className="profile-tooltip-item"><span className="profile-tooltip-term">U.S. permanent resident:</span> Green card holder with lawful permanent residence</div>
                          <div className="profile-tooltip-item"><span className="profile-tooltip-term">Citizen of non-U.S. country:</span> International students and foreign nationals</div>
                          <div className="profile-tooltip-item"><span className="profile-tooltip-term">U.S. resident:</span> Other U.S. residents including refugees, asylees, DACA, DED, TPS, undocumented students, or those with pending status</div>
                        </div>
                      )}
                    </span>
                  </label>
                  <select
                    className="profile-form-select"
                    value={formData.citizenship_status}
                    onChange={(e) => handleInputChange('citizenship_status', e.target.value)}
                  >
                    <option value="">Select citizenship status...</option>
                    {citizenshipOptions.map(option => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <div className="profile-save-section">
              <button
                className="profile-save-button"
                onClick={handleSaveProfile}
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save Profile'}
              </button>
            </div>
          </div>
        )}
      </div>

      <CollapsibleSection
        title="Activities"
        description="Extracurricular activities, leadership roles, and community involvement"
        defaultExpanded={false}
      >
        <ActivitiesInner />
      </CollapsibleSection>

      {/* <CollapsibleSection
        title="Family Information"
        description="Tell us about your family for college application requirements"
        defaultExpanded={false}
      >
        <FamilySection />
      </CollapsibleSection>

      <CollapsibleSection
        title="Education"
        description="Academic history, coursework, and educational achievements"
        defaultExpanded={false}
      >
        <EducationSection />
      </CollapsibleSection>

      <CollapsibleSection
        title="Testing"
        description="Standardized tests, advanced courses, and language proficiency assessments"
        defaultExpanded={false}
      >
        <TestingSection />
      </CollapsibleSection>

      <CollapsibleSection
        title="Career"
        description="Explore careers, majors, and career paths with AI-powered insights"
        defaultExpanded={false}
      >
        <CareerSection />
      </CollapsibleSection>

      <CollapsibleSection
        title="Preferences & Interests"
        description="Define your academic interests, intended majors, and career goals"
        defaultExpanded={false}
      >
        <PreferencesSection />
      </CollapsibleSection> */}
    </div>
  );
};

const ActivitiesInner: React.FC = () => {
  const {
    activities,
    missingInfo,
    isLoading,
    isAnalyzing,
    isCreating,
    analyzeActivity,
    createActivity,
    updateActivity,
    deleteActivity,
    reorderActivities,
  } = useActivityData();

  const handleAnalyze = async (description: string) => {
    const result = await analyzeActivity({ description });
    const { id: _id, user_id: _uid, confidence_score: _cs, order_index: _oi, ...activityFields } = result.activity;
    await createActivity({ ...activityFields, raw_description: description });
  };

  const handleEdit = async (activity: Activity) => {
    const { id, user_id: _uid, confidence_score: _cs, order_index: _oi, raw_description: _rd, ...editableFields } = activity;
    await updateActivity({ id, data: editableFields });
  };

  return (
    <ActivityCardsDisplay
      activities={activities}
      isLoading={isLoading}
      isAnalyzing={isAnalyzing || isCreating}
      missingInfo={missingInfo}
      onAnalyze={handleAnalyze}
      onEdit={handleEdit}
      onDelete={deleteActivity}
      onReorder={async (newOrder) => { await reorderActivities(newOrder); }}
    />
  );
};

export default ProfileTab;
